import { useEffect, useRef } from "react";

// Loads the YouTube iframe API once, then resolves.
let ytApiPromise = null;
function loadYouTubeAPI() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
  });
  return ytApiPromise;
}

function extractVideoId(url) {
  const m = url.match(/\/embed\/([^?/]+)/);
  return m ? m[1] : null;
}

const WATCH_THRESHOLD_PCT = 0.9; // 90% counts as watched

export default function VideoPlayer({ src, title, onWatched }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const firedRef = useRef(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    firedRef.current = false;

    const videoId = extractVideoId(src);
    if (!videoId || !containerRef.current) return;

    const trigger = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (onWatched) onWatched();
    };

    (async () => {
      const YT = await loadYouTubeAPI();
      if (cancelled) return;

      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch {}
        playerRef.current = null;
      }

      playerRef.current = new YT.Player(containerRef.current, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, cc_load_policy: 1 },
        events: {
          onStateChange: (e) => {
            // Full-play end still counts
            if (e.data === YT.PlayerState.ENDED) trigger();

            // While playing, poll for the 90% threshold
            if (e.data === YT.PlayerState.PLAYING && !pollRef.current) {
              pollRef.current = setInterval(() => {
                try {
                  const p = playerRef.current;
                  if (!p || firedRef.current) return;
                  const dur = p.getDuration?.() || 0;
                  const cur = p.getCurrentTime?.() || 0;
                  if (dur > 0 && cur / dur >= WATCH_THRESHOLD_PCT) trigger();
                } catch {}
              }, 2000);
            }
            if (e.data === YT.PlayerState.PAUSED && pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          },
        },
      });
    })();

    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch {}
        playerRef.current = null;
      }
    };
  }, [src, onWatched]);

  return (
    <div className="relative w-full rounded-md overflow-hidden border border-zinc-200 bg-black" style={{ paddingTop: "56.25%" }}>
      <div
        ref={containerRef}
        className="absolute inset-0 w-full h-full"
        title={title}
        data-testid="module-video-iframe"
      />
    </div>
  );
}
